import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { AuthorizationClient, ClaimDto } from "../lib/ShiOneClient";
import { useApi } from "../v2/src/utils";
import { useQuery } from "@tanstack/react-query";
import { useAuth0 } from "@auth0/auth0-react";
import {
  claimTypes,
  ClaimTypes,
  hasMultipleTenantAccounts,
  RoleTypes,
  roleTypes,
  ShiOnePermission,
} from "./authorizationTypes";
import { isImpersonatingAccount } from "shared-ui/src/utils/clientmanagement";
import { syncDemoDataFlag } from "shared-ui/src/utils/demoDataHelpers";

const defaultClaims = [] as ClaimDto[];

type PermissionResourceClaimValue = {
  permission: string;
  resourceIdentifier: number;
};

function parseNumericClaimValue(
  claimValue: string | undefined,
): number | undefined {
  const parsed = Number(claimValue);
  return Number.isNaN(parsed) ? undefined : parsed;
}

function parseBooleanClaimsValue(claimValue: string | undefined): boolean {
  return ["true", "1"].includes(claimValue?.toLowerCase() ?? "");
}

type ClaimsContextSnapshot = {
  tenantId: string;
  orgUnitIdCurrent: string;
};

export function shouldReloadForContextChange(
  previous: ClaimsContextSnapshot | null,
  next: ClaimsContextSnapshot,
): boolean {
  return (
    previous !== null &&
    next.tenantId !== "" &&
    (previous.tenantId !== next.tenantId ||
      previous.orgUnitIdCurrent !== next.orgUnitIdCurrent)
  );
}

export const ClaimsContext = createContext({
  claims: defaultClaims,
  claimsLoading: false,
  claimsFetching: false,
  claimsError: false,
  refetchClaims: () => {},
  getClaimValue: (_claim: ClaimTypes): string | undefined => undefined,
  getClaimValueAsNumber: (_claim: ClaimTypes): number | undefined => undefined,
  getClaimValueAsBoolean: (_claim: ClaimTypes): boolean => false,
  hasMultipleAccounts: (): boolean => false,
  getUserDisplayName: (): string => "",
  hasRoleIn: (..._roles: RoleTypes[]): boolean => false,
  hasPermission: (_permission: ShiOnePermission): boolean => false,
  hasResourcePermission: (
    _permission: string,
    _resourceIdentifier: number,
  ): boolean => false,
  getPermissionResourceIdentifiers: (_permission: string): number[] => [],
  isCustomerUser: (): boolean => false,
  isTrueCustomerAccount: (): boolean => false,
  isCustomerAdmin: (): boolean => false,
  isShiAdmin: (): boolean => false,
  isShi: (): boolean => false,
  isVirtualAdmin: (): boolean => false,
  isGuestUser: (): boolean => false,
  getPermissions: (): string[] => [],
});

export type HasPermissionFunctionType = (
  permission: ShiOnePermission,
) => boolean;

export const ClaimsProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { isAuthenticated } = useAuth0();

  let api = useApi(AuthorizationClient);

  let response = useQuery(["claims"], () => api.getUserClaims(), {
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: "always",
    structuralSharing: (oldData, newData) => {
      if (
        Array.isArray(oldData) &&
        Array.isArray(newData) &&
        oldData.length === newData.length &&
        oldData.every(
          (claim, index) =>
            claim.claimType === newData[index]?.claimType &&
            claim.claimValue === newData[index]?.claimValue,
        )
      ) {
        return oldData;
      }

      return newData;
    },
  });

  const claims = response.data ?? defaultClaims;
  const lastSeenContext = useRef<ClaimsContextSnapshot | null>(null);

  const refetchClaims = useCallback(() => {
    void response.refetch();
  }, [response.refetch]);

  useEffect(() => {
    if (claims.length === 0) {
      return;
    }

    const nextContext = {
      tenantId:
        claims.find((claim) => claim.claimType === claimTypes.tenantId)
          ?.claimValue ?? "",
      orgUnitIdCurrent:
        claims.find((claim) => claim.claimType === claimTypes.orgUnitIdCurrent)
          ?.claimValue ?? "",
    };
    if (nextContext.tenantId === "") {
      return;
    }

    const previousContext = lastSeenContext.current;
    lastSeenContext.current = nextContext;

    if (shouldReloadForContextChange(previousContext, nextContext)) {
      // A full reload reinitializes app context after a switch outside this tab.
      window.location.href = window.location.origin;
    }
  }, [claims, response.dataUpdatedAt]);

  useEffect(() => {
    const accountIdClaim = claims.find(
      (claim) => claim.claimType === claimTypes.accountId,
    )?.claimValue;
    const accountId = parseNumericClaimValue(accountIdClaim);

    if (accountId != null && accountId > 0) {
      syncDemoDataFlag(accountId);
    }
  }, [claims]);

  const providerValues = useMemo(() => {
    const getClaimValue = (claimType: ClaimTypes) => {
      let claim = claims.find(
        (claim) => claim.claimType === claimTypes[claimType],
      );
      return claim ? claim.claimValue : undefined;
    };

    const getClaims = (claimType: ClaimTypes) => {
      return claims.filter(
        (claim) => claim.claimType === claimTypes[claimType],
      );
    };

    const getClaimValueAsNumber = (claimType: ClaimTypes) => {
      return parseNumericClaimValue(getClaimValue(claimType));
    };

    const getClaimValueAsBoolean = (claimType: ClaimTypes) => {
      return parseBooleanClaimsValue(getClaimValue(claimType));
    };

    const hasMultipleAccounts = () => hasMultipleTenantAccounts(claims);

    const getUserDisplayName = () => {
      return [getClaimValue("firstName"), getClaimValue("lastName")]
        .filter(Boolean)
        .join(" ");
    };

    const hasRoleIn = (...roles: RoleTypes[]) => {
      let hasRole = false;
      let userRoles = getClaimValue("roles") ?? "0";
      let userRolesInt = parseInt(userRoles);
      if (isNaN(userRolesInt)) {
        return hasRole;
      }
      roles.forEach((role) => {
        if ((roleTypes[role] & userRolesInt) === roleTypes[role]) {
          hasRole = true;
        }
      });
      return hasRole;
    };

    const isGuestUser = () => {
      return !!getClaimValue("isGuestUser");
    };

    const isCustomerAdmin = () => {
      return hasRoleIn("CustomerAdmin");
    };

    const isCustomerUser = () => {
      return hasRoleIn("CustomerUser");
    };

    const isShiAdmin = () => {
      return hasRoleIn("ShiAdmin");
    };

    const isShi = () => {
      return !!getClaimValue("isShi");
    };

    const isVirtualAdmin = () => {
      return hasRoleIn("ShiImpersonateVirtualAdmin");
    };

    const getPermissions = () => {
      return getClaims("permission").map((claim) => claim.claimValue ?? "");
    };

    const isTrueCustomerAccount = () => {
      return hasRoleIn("CustomerUser") && !isImpersonatingAccount();
    };

    const hasPermission = (permission: ShiOnePermission) => {
      let hasPermission: boolean;
      const permissions = getClaims("permission").map(
        (claim) => claim.claimValue ?? "",
      );

      hasPermission = permissions.some((p) => {
        return p === permission;
      });
      return hasPermission;
    };

    const permissionResourceClaims = getClaims("permissionResource")
      .map((claim) => parsePermissionResourceClaim(claim.claimValue))
      .filter(
        (claim): claim is PermissionResourceClaimValue => claim !== undefined,
      );

    const getPermissionResourceIdentifiers = (permission: string) => {
      return Array.from(
        new Set(
          permissionResourceClaims
            .filter((claim) => claim.permission === permission)
            .map((claim) => claim.resourceIdentifier),
        ),
      );
    };

    const hasResourcePermission = (
      permission: string,
      resourceIdentifier: number,
    ) => {
      if (resourceIdentifier <= 0) {
        return false;
      }

      return permissionResourceClaims.some((claim) => {
        return (
          claim.permission === permission &&
          claim.resourceIdentifier === resourceIdentifier
        );
      });
    };

    return {
      claims,
      refetchClaims,
      getClaimValue,
      getClaimValueAsNumber,
      getClaimValueAsBoolean,
      hasMultipleAccounts,
      getUserDisplayName,
      hasRoleIn: hasRoleIn,
      hasPermission,
      hasResourcePermission,
      getPermissionResourceIdentifiers,
      getPermissions,
      isCustomerUser,
      isTrueCustomerAccount,
      isCustomerAdmin,
      isShiAdmin,
      isShi,
      isVirtualAdmin,
      isGuestUser,
    };
  }, [claims, refetchClaims]);

  return (
    <ClaimsContext.Provider
      value={{
        ...providerValues,
        claimsLoading: response.isLoading,
        claimsFetching: response.isFetching,
        claimsError: response.isError,
      }}
    >
      {children}
    </ClaimsContext.Provider>
  );
};

export function useClaims() {
  return useContext(ClaimsContext);
}

function parsePermissionResourceClaim(
  claimValue: string | undefined,
): PermissionResourceClaimValue | undefined {
  if (!claimValue) {
    return undefined;
  }

  const separatorIndex = claimValue.indexOf(":");
  if (separatorIndex <= 0 || separatorIndex === claimValue.length - 1) {
    return undefined;
  }

  const permission = claimValue.slice(0, separatorIndex);
  const rawResourceIdentifier = claimValue.slice(separatorIndex + 1);
  const resourceIdentifier = Number(rawResourceIdentifier);
  if (
    permission.trim() === "" ||
    !Number.isInteger(resourceIdentifier) ||
    resourceIdentifier <= 0
  ) {
    return undefined;
  }

  return {
    permission,
    resourceIdentifier,
  };
}
